// src/components/SalesDashboard/SalesRouting.js
import React, { useState } from 'react';
import {
  LayoutDashboard, Users, FileText, ShoppingCart, Receipt,
  Menu, LogOut, Gem, ChevronRight, BookOpen, Package, Truck,
} from 'lucide-react';
import CustomerList from './CustomerList';
import CustomerDetail from './CustomerDetail';
import SupplierList from './SupplierList';
import QuotationList from './QuotationList';
import QuotationCreate from './QuotationCreate';
import QuotationDetail from './QuotationDetail';
import SalesOrderList from './SalesOrderList';
import SalesOrderDetail from './SalesOrderDetail';
import SalesOrderCreate from './SalesOrderCreate';
import SalesDashboard from './SalesDashboard';
import DesignCatalogue from './DesignCatalogue';
import StoreInventory from './StoreInventory';
import CompletedOrders from './CompletedOrders';
import ModuleGoalsDashboard from '../Goals/ModuleGoalsDashboard';
import LogisticsProviders from '../Admin/LogisticsProviders';
import { getStoredRole } from '../api';

const SALES_STYLES = `
  .sales-layout { display: flex; height: 100vh; overflow: hidden; font-family: 'Inter', sans-serif; }
  .sales-sidebar {
    width: 260px; min-width: 260px; background: linear-gradient(180deg,#1e1b4b 0%,#312e81 50%,#1e293b 100%);
    color: white; display: flex; flex-direction: column; transition: transform .3s ease; z-index: 20;
    box-shadow: 4px 0 24px rgba(0,0,0,.3);
  }
  .sales-sidebar.closed { transform: translateX(-100%); position: fixed; height: 100%; }
  .sales-main { flex: 1; display: flex; flex-direction: column; overflow: hidden; background: #f8fafc; }
  .sales-header {
    height: 64px; background: white; border-bottom: 1px solid #e2e8f0;
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 24px; box-shadow: 0 1px 4px rgba(0,0,0,.06); z-index: 10; flex-shrink: 0;
  }
  .sales-content { flex: 1; overflow-y: auto; padding: 24px; }
  .sales-nav-item {
    display: flex; align-items: center; gap: 12px; padding: 10px 16px; border-radius: 10px;
    cursor: pointer; transition: all .2s; border: none; background: transparent; color: #a5b4fc;
    font-size: 14px; font-weight: 500; width: 100%; text-align: left;
  }
  .sales-nav-item:hover { background: rgba(255,255,255,.08); color: white; }
  .sales-nav-item.active { background: rgba(255,255,255,.12); color: white; box-shadow: inset 0 0 0 1px rgba(255,255,255,.1); }
  .sales-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.4); z-index: 19; display: none; }
  .sales-overlay.visible { display: block; }
  @media(max-width:768px) {
    .sales-sidebar { position: fixed; height: 100%; transform: translateX(-100%); }
    .sales-sidebar.open { transform: translateX(0); }
  }
  @media(min-width:769px) { .sales-sidebar { position: relative; transform: none !important; } }
`;

const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'catalogue', label: 'Design Catalogue', icon: BookOpen },
  { id: 'customers', label: 'Customers', icon: Users },
  { id: 'suppliers', label: 'Suppliers', icon: Truck },
  { id: 'quotations', label: 'Quotations', icon: FileText },
  { id: 'orders', label: 'Sales Orders', icon: ShoppingCart },
  { id: 'completed_orders', label: 'Completed Orders', icon: ShoppingCart },
  { id: 'store', label: 'Store Inventory', icon: Package },
  { id: 'logistics', label: 'Logistics', icon: Truck },
  { id: 'goals', label: 'Goals & Tasks', icon: FileText },
  { id: 'invoices', label: 'Invoices', icon: Receipt },
];

export default function SalesRouting({ user, onLogout }) {
  const localRole = String(
    getStoredRole() ||
    user?.role ||
    ''
  ).toUpperCase();
  const isAdmin = ['ADMIN', 'ORG_ADMIN', 'SUPER_ADMIN', 'OWNER'].includes(localRole);
  const isSalesManager = isAdmin || localRole === 'SALES_MANAGER';

  const [activePage, setActivePage] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Customer sub-state
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);

  // Quotation sub-state
  const [quotationView, setQuotationView] = useState('list'); // 'list' | 'create' | 'detail' | 'new-version'
  const [selectedQuotationId, setSelectedQuotationId] = useState(null);
  const [catalogueDesign, setCatalogueDesign] = useState(null); // design pre-selected from catalogue

  // Sales Order sub-state
  const [orderView, setOrderView] = useState('list'); // 'list' | 'create' | 'detail'
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const resetOrders = () => { setOrderView('list'); setSelectedOrderId(null); };

  const handleNavigate = (page, params = {}) => {
    if (params.customerId) setSelectedCustomerId(params.customerId);
    setActivePage(page);
    setSidebarOpen(false);
  };

  const resetQuotations = () => {
    setQuotationView('list');
    setSelectedQuotationId(null);
    setCatalogueDesign(null);
  };

  const handleSelectFromCatalogue = (design) => {
    setCatalogueDesign(design);
    setQuotationView('create');
    handleNavigate('quotations');
  };

  const renderContent = () => {
    switch (activePage) {
      // ── Customers ──────────────────────────────────────────────────────────
      case 'customers':
        if (selectedCustomerId) {
          return (
            <CustomerDetail
              customerId={selectedCustomerId}
              onBack={() => setSelectedCustomerId(null)}
            />
          );
        }
        return (
          <CustomerList
            onViewDetail={(id) => setSelectedCustomerId(id)}
          />
        );
      case 'suppliers':
        return <SupplierList />;

      // ── Design Catalogue ───────────────────────────────────────────────────
      case 'catalogue':
        return (
          <DesignCatalogue onSelectDesign={handleSelectFromCatalogue} />
        );

      // ── Store Inventory ────────────────────────────────────────────────────
      case 'store':
        return <StoreInventory />;

      case 'completed_orders':
        return <CompletedOrders />;
      case 'logistics':
        return <LogisticsProviders />;

      // ── Quotations ─────────────────────────────────────────────────────────
      case 'quotations':
        if (quotationView === 'create') {
          return (
            <QuotationCreate
              onBack={resetQuotations}
              initialDesign={catalogueDesign}
              onCreated={(qt) => {
                setCatalogueDesign(null);
                setSelectedQuotationId(qt.id);
                setQuotationView('detail');
              }}
            />
          );
        }
        if (quotationView === 'detail' && selectedQuotationId) {
          return (
            <QuotationDetail
              quotationId={selectedQuotationId}
              user={user}
              onBack={resetQuotations}
              onCreateNewVersion={(qtId) => {
                alert('New version creation: implement QuotationNewVersion component or reuse QuotationCreate with existing items pre-filled.');
              }}
              onConvertToOrder={(orderId) => {
                // Navigate directly to the new order's detail page
                setSelectedOrderId(orderId);
                setOrderView('detail');
                setActivePage('orders');
              }}
            />
          );
        }
        return (
          <QuotationList
            user={user}
            userRole={localRole}
            isAdmin={isAdmin}
            isSalesManager={isSalesManager}
            onViewDetail={(id) => {
              setSelectedQuotationId(id);
              setQuotationView('detail');
            }}
            onCreateNew={() => setQuotationView('create')}
          />
        );

      // ── Sales Orders ───────────────────────────────────────────────────────
      case 'orders':
        if (orderView === 'create') {
          return (
            <SalesOrderCreate
              onBack={resetOrders}
              onCreated={(order) => {
                setSelectedOrderId(order.order_id);
                setOrderView('detail');
              }}
            />
          );
        }
        if (orderView === 'detail' && selectedOrderId) {
          return (
            <SalesOrderDetail
              orderId={selectedOrderId}
              onBack={resetOrders}
            />
          );
        }
        return (
          <SalesOrderList
            onViewDetail={(id) => { setSelectedOrderId(id); setOrderView('detail'); }}
            onCreateNew={() => setOrderView('create')}
          />
        );

      // ── Other ──────────────────────────────────────────────────────────────
      case 'dashboard':
        return (
          <SalesDashboard
            onNavigate={(page, params) => {
              if (params?.orderId) {
                setSelectedOrderId(params.orderId);
                setOrderView('detail');
              }
              handleNavigate(page);
            }}
            onNewQuotation={() => {
              setQuotationView('create');
              handleNavigate('quotations');
            }}
            onNewOrder={() => {
              setOrderView('create');
              handleNavigate('orders');
            }}
          />
        );
      case 'invoices':
        return <ComingSoon title="Invoices" />;
      case 'goals':
        return <ModuleGoalsDashboard moduleName="SALES" />;
      default:
        return null;
    }
  };

  // Derive header title
  const getPageTitle = () => {
    if (activePage === 'catalogue') return 'Design Catalogue';
    if (activePage === 'completed_orders') return 'Completed Orders';
    if (activePage === 'store') return 'Store Inventory';
    if (activePage === 'logistics') return 'Logistics';
    if (activePage === 'customers' && selectedCustomerId) return 'Customer Detail';
    if (activePage === 'suppliers') return 'Supplier Master';
    if (activePage === 'quotations') {
      if (quotationView === 'create') return 'New Quotation';
      if (quotationView === 'detail') return 'Quotation Detail';
    }
    if (activePage === 'orders') {
      if (orderView === 'create') return 'New Sales Order';
      if (orderView === 'detail') return 'Order Detail';
    }
    return NAV_ITEMS.find(n => n.id === activePage)?.label || 'Sales';
  };

  return (
    <>
      <style>{SALES_STYLES}</style>
      <div className="sales-layout prod-module-root">
        {/* Overlay */}
        <div
          className={`sales-overlay ${sidebarOpen ? 'visible' : ''}`}
          onClick={() => setSidebarOpen(false)}
        />

        {/* Sidebar */}
        <aside className={`sales-sidebar ${sidebarOpen ? 'open' : ''}`}>
          {/* Logo */}
          <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid rgba(255,255,255,.1)', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, background: 'linear-gradient(135deg,#f59e0b,#d97706)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Gem size={22} color="white" />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 900, letterSpacing: '-.5px', color: 'white' }}>
                ZILVERRA<span style={{ color: '#818cf8' }}>JEWELS</span>
              </div>
              <div style={{ fontSize: 11, color: '#a5b4fc' }}>Sales Module</div>
            </div>
          </div>

          {/* Nav */}
          <nav style={{ padding: '16px 12px', display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '1px', padding: '0 4px 8px' }}>
              Sales Hub
            </div>
            {NAV_ITEMS.map(item => (
              <button
                key={item.id}
                className={`sales-nav-item ${activePage === item.id ? 'active' : ''}`}
                onClick={() => {
                  setSelectedCustomerId(null);
                  resetQuotations();
                  resetOrders();
                  handleNavigate(item.id);
                }}
              >
                <item.icon size={18} />
                {item.label}
                {activePage === item.id && <ChevronRight size={14} style={{ marginLeft: 'auto' }} />}
              </button>
            ))}
          </nav>

          {/* User strip */}
          <div style={{ margin: '12px', padding: '12px', background: 'rgba(0,0,0,.25)', borderRadius: 10, border: '1px solid rgba(255,255,255,.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'white' }}>
                {(user?.username || 'S').charAt(0).toUpperCase()}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'white' }}>{user?.username || 'Sales User'}</div>
                <div style={{ fontSize: 11, color: '#a5b4fc' }}>Sales Manager</div>
              </div>
            </div>
          </div>
        </aside>

        {/* Main */}
        <main className="sales-main">
          {/* Header */}
          <header className="sales-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <button
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1a1a1a', padding: 4, display: 'flex', alignItems: 'center' }}
                onClick={() => setSidebarOpen(v => !v)}
              >
                <Menu size={22} />
              </button>

              {/* Breadcrumb back links */}
              {activePage === 'customers' && selectedCustomerId && (
                <button
                  onClick={() => setSelectedCustomerId(null)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  ← Customers
                </button>
              )}
              {activePage === 'quotations' && quotationView !== 'list' && (
                <button
                  onClick={resetQuotations}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  ← Quotations
                </button>
              )}
              {activePage === 'orders' && orderView !== 'list' && (
                <button
                  onClick={resetOrders}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  ← Sales Orders
                </button>
              )}

              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#000', margin: 0 }}>{getPageTitle()}</h2>
            </div>
            <button
              onClick={() => window.confirm('Logout?') && onLogout?.()}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', border: '1px solid #fecaca', background: '#fff5f5', color: '#dc2626', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
            >
              <LogOut size={16} />
              Logout
            </button>
          </header>

          {/* Content */}
          <div className="sales-content">
            {renderContent()}
          </div>
        </main>
      </div>
    </>
  );
}

function ComingSoon({ title }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, gap: 12, color: '#1a1a1a' }}>
      <div style={{ fontSize: 48 }}>🚧</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: '#1a1a1a' }}>{title}</div>
      <div style={{ fontSize: 14 }}>Coming soon — next sprint</div>
    </div>
  );
}
